import type { NavigateFunction } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { NotificationAggregateItem, NotificationType } from '@magimp/shared';
import { Eyebrow } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import type { NotificationsListSplit } from '../hooks/useNotificationsList.js';
import { buildNotificationHref } from '../utils/routing.js';

interface PanelProps {
  loading: boolean;
  error: Error | null;
  split: NotificationsListSplit | null;
  onRowSelect: (row: NotificationAggregateItem) => void;
  navigate: NavigateFunction;
}

export function NotificationBellPanel({
  loading,
  error,
  split,
  onRowSelect,
  navigate,
}: PanelProps) {
  const { t } = useTranslation('shell');

  if (loading) {
    return (
      <div
        role="status"
        style={{ padding: '12px 14px', fontSize: 13, color: 'var(--fg-2)' }}
        data-testid="bell-panel-loading"
      >
        {t('bell.loading')}
      </div>
    );
  }
  if (error) {
    return (
      <div
        role="alert"
        style={{ padding: '12px 14px', fontSize: 13, color: 'var(--status-error-fg)' }}
        data-testid="bell-panel-error"
      >
        {t('bell.loadError')}: {errorMessage(error)}
      </div>
    );
  }
  if (!split || (split.unread.length === 0 && split.recent.length === 0)) {
    return (
      <div
        style={{ padding: '12px 14px', fontSize: 13, color: 'var(--fg-2)' }}
        data-testid="bell-panel-empty"
      >
        {t('bell.empty')}
      </div>
    );
  }

  return (
    <div data-testid="bell-panel" style={{ maxHeight: 480, overflowY: 'auto' }}>
      {split.unread.length > 0 && (
        <NotificationGroup
          headerKey="bell.unreadHeader"
          rows={split.unread}
          onRowSelect={onRowSelect}
          navigate={navigate}
        />
      )}
      {split.recent.length > 0 && (
        <NotificationGroup
          headerKey="bell.recentHeader"
          rows={split.recent}
          onRowSelect={onRowSelect}
          navigate={navigate}
        />
      )}
    </div>
  );
}

interface GroupProps {
  headerKey: 'bell.unreadHeader' | 'bell.recentHeader';
  rows: NotificationAggregateItem[];
  onRowSelect: (row: NotificationAggregateItem) => void;
  navigate: NavigateFunction;
}

function NotificationGroup({ headerKey, rows, onRowSelect, navigate }: GroupProps) {
  const { t } = useTranslation('shell');
  return (
    <div>
      <Eyebrow style={{ padding: '8px 12px', borderTop: '1px solid var(--border-2)' }}>
        {headerKey === 'bell.unreadHeader' ? t('bell.unreadHeader') : t('bell.recentHeader')}
      </Eyebrow>
      {rows.map((row) => (
        <NotificationRow
          key={row.id}
          row={row}
          onSelect={() => {
            onRowSelect(row);
            navigate(
              buildNotificationHref({
                entityType: row.entityType,
                entityId: row.entityId,
                routingTab: row.routingTab,
              }),
            );
          }}
        />
      ))}
    </div>
  );
}

interface RowProps {
  row: NotificationAggregateItem;
  onSelect: () => void;
}

function NotificationRow({ row, onSelect }: RowProps) {
  const { t } = useTranslation('shell');
  const isUnread = row.unreadCount > 0;
  const description = describeNotification(row, t);
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onSelect}
      data-testid={`bell-row-${row.id}`}
      data-unread={isUnread ? 'true' : 'false'}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        background: isUnread ? 'var(--bg-2)' : 'transparent',
        border: 'none',
        borderTop: '1px solid var(--border-2)',
        cursor: 'pointer',
        color: 'var(--fg-1)',
        fontSize: 13,
        textAlign: 'left',
      }}
    >
      <span style={{ fontWeight: 500, minWidth: 60 }}>{row.entityId}</span>
      <span style={{ flex: 1, color: isUnread ? 'var(--fg-1)' : 'var(--fg-2)' }}>
        {description}
      </span>
      {isUnread && row.unreadCount > 0 && (
        <span
          aria-hidden="true"
          style={{
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 9,
            background: 'var(--status-error-fg)',
            color: 'var(--fg-invert)',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '18px',
            textAlign: 'center',
          }}
        >
          {row.unreadCount}
        </span>
      )}
    </button>
  );
}

function describeNotification(row: NotificationAggregateItem, t: TFunction<'shell'>): string {
  const typeKey: NotificationType = row.notificationType;
  // WHY: i18n keys are statically typed; switch on the closed
  //   NotificationType enum so the compiler can pin every branch.
  if (row.unreadCount > 0) {
    switch (typeKey) {
      case 'chat':
        return t('bell.unreadDescription.chat', { count: row.unreadCount });
      case 'rate':
        return t('bell.unreadDescription.rate', { count: row.unreadCount });
      case 'rejection':
        return t('bell.unreadDescription.rejection', { count: row.unreadCount });
      case 'insurance':
        return t('bell.unreadDescription.insurance', { count: row.unreadCount });
      case 'responsibleStatus':
        return t('bell.unreadDescription.responsibleStatus');
      case 'lead_callback':
        return t('bell.unreadDescription.leadCallback', { count: row.unreadCount });
      case 'cargo_problem':
        return t('bell.unreadDescription.cargoProblem', { count: row.unreadCount });
    }
  }
  switch (typeKey) {
    case 'chat':
      return t('bell.recentDescription.chat');
    case 'rate':
      return t('bell.recentDescription.rate');
    case 'rejection':
      return t('bell.recentDescription.rejection');
    case 'insurance':
      return t('bell.recentDescription.insurance');
    case 'responsibleStatus':
      return t('bell.recentDescription.responsibleStatus');
    case 'lead_callback':
      // WHY (#1362): `emitLeadCallback` persists live LEAD-targeted
      //   aggregates, so the bell needs dedicated callback copy.
      return t('bell.recentDescription.leadCallback');
    case 'cargo_problem':
      return t('bell.recentDescription.cargoProblem');
  }
}
